import React, { useState, useEffect } from 'react';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { Form, Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSpinner, FaPlus, FaDownload, FaMoneyBillWave, FaUser, FaCalendarAlt, FaUserCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuthContext } from '../../context/AuthContext';
import { fundApi } from '../../api/funds';
import { pledgeApi } from '../../api/pledges';
import './Michango.css';
import { useDebounce } from '../../hooks/useDebounce';

const Michango = () => {
  const { user, events, currentEvent, isAuthenticated } = useAuthContext();

  // State for data
  const [contributions, setContributions] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI State
  const [perPage, setPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, 500);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, searchTerm]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state for new contribution
  const [newContribution, setNewContribution] = useState({
    pledge_id: '',
    amount: '',
    description: '',
    received_by: ''
  });

  // Fetch data on component mount
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchContributions();
      fetchPledges();
    }
  }, [isAuthenticated, currentEvent]);

  const fetchContributions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fundApi.getFunds();
      let funds = response?.data || response || [];

      // Ensure funds is an array
      if (!Array.isArray(funds)) {
        funds = [];
      }

      // Transform data to match component structure
      const formattedContributions = funds.map(fund => ({
        id: fund.id,
        full_name: String(fund.pledger || 'N/A'),
        pledge_id: fund.pledge_id,
        amount: `TZS ${(parseFloat(fund.amount) || 0).toLocaleString()}`,
        amount_raw: parseFloat(fund.amount) || 0,
        description: typeof fund.description === 'string' ? fund.description : '-',
        received_by: String(fund.recorder?.full_name || fund.received_by || 'N/A'),
        date: fund.created_at ? new Date(fund.created_at).toLocaleDateString('sw-TZ') : 'N/A',
        recorded_by: fund.recorded_by,
        status: fund.status
      }));

      setContributions(formattedContributions);
    } catch (err) {
      console.error('Error fetching contributions:', err);
      setContributions([]);
      if (err.response?.status !== 404) {
        setError('Failed to load contributions. Please refresh the page.');
        toast.error('Failed to load contributions');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPledges = async () => {
    try {
      const response = await pledgeApi.getPledges();
      let pledgesList = response?.data || response || [];

      if (!Array.isArray(pledgesList)) {
        pledgesList = [];
      }

      setPledges(pledgesList);
    } catch (err) {
      console.error('Error fetching pledges:', err);
      setPledges([]);
    }
  };

  // Add new contribution
  const handleAddContribution = async () => {
    if (!newContribution.pledge_id) {
      toast.warning('Tafadhali chagua aliyeahidi');
      return;
    }

    if (!newContribution.amount || parseFloat(newContribution.amount) <= 0) {
      toast.warning('Tafadhali ingiza kiasi cha mchango');
      return;
    }

    setSubmitting(true);
    try {
      const contributionData = {
        pledge_id: parseInt(newContribution.pledge_id),
        amount: parseFloat(newContribution.amount),
        description: newContribution.description || null,
        received_by: newContribution.received_by || user?.full_name || 'Admin',
        recorded_by: user?.id
      };

      const response = await fundApi.createFund(contributionData);

      if (handleApiResponse(response)) {
        setShowModal(false);
        resetForm();
        fetchContributions();
        fetchPledges(); // Refresh pledges to update collected amounts
      }
    } catch (err) {
      console.error('Error adding contribution:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Update contribution
  const handleUpdateContribution = async () => {
    if (!selectedContribution) return;

    setSubmitting(true);
    try {
      const updateData = {
        amount: parseFloat(selectedContribution.amount_raw),
        description: selectedContribution.description !== '-' ? selectedContribution.description : null,
        received_by: selectedContribution.received_by
      };

      const response = await fundApi.updateFund(selectedContribution.id, updateData);

      if (handleApiResponse(response)) {
        setShowEditModal(false);
        setSelectedContribution(null);
        fetchContributions();
      }
    } catch (err) {
      console.error('Error updating contribution:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete contribution
  const handleDeleteContribution = async () => {
    if (!selectedContribution) return;

    setSubmitting(true);
    try {
      const response = await fundApi.deleteFund(selectedContribution.id);

      if (handleApiResponse(response)) {
        setShowDeleteModal(false);
        setSelectedContribution(null);
        fetchContributions();
        fetchPledges(); // Refresh pledges to update collected amounts
      }
    } catch (err) {
      console.error('Error deleting contribution:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Export to Excel
  const handleExportToExcel = async () => {
    try {
      // You can implement Excel export here
      toast.info('Export to Excel feature coming soon');
    } catch (err) {
      console.error('Error exporting:', err);
      toast.error('Failed to export data');
    }
  };

  const resetForm = () => {
    setNewContribution({
      pledge_id: '',
      amount: '',
      description: '',
      received_by: ''
    });
  };

  const columns = [
    {
      name: '#',
      cell: (row, index) => index + 1,
      width: '60px'
    },
    {
      name: 'Jina Kamili',
      selector: (row) => row.full_name,
      sortable: true,
      grow: 2
    },
    {
      name: 'Kiasi (TZS)',
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => (
        <span className="amount-cell">{row.amount}</span>
      )
    },
    {
      name: 'Maelezo',
      selector: (row) => row.description,
      sortable: true
    },
    {
      name: 'Aliyepokea',
      selector: (row) => row.received_by,
      sortable: true
    },
    {
      name: 'Tarehe',
      selector: (row) => row.date,
      sortable: true
    },
    {
      name: 'Vitendo',
      cell: (row) => (
        <div className="action-buttons">
          <FaEdit
            className="text-primary action-icon"
            onClick={() => {
              setSelectedContribution(row);
              setShowEditModal(true);
            }}
            title="Hariri Mchango"
          />
          <FaTrash
            className="text-danger action-icon"
            onClick={() => {
              setSelectedContribution(row);
              setShowDeleteModal(true);
            }}
            title="Futa Mchango"
          />
        </div>
      ),
      width: '100px'
    },
  ];

  const filteredData = contributions.filter(contribution =>
    String(contribution.full_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Custom empty state component
  const EmptyState = () => (
    <div className="empty-state-container">
      <div className="empty-state-icon">
        <FaMoneyBillWave size={64} className="text-muted" />
      </div>
      <h5 className="empty-state-title">Hakuna Michango</h5>
      <p className="empty-state-message">
        Bado hakuna mchango uliorekodiwa. Bonyeza kitufe cha "Ongeza Mchango" kuanza.
      </p>
      <Button
        variant="primary"
        onClick={() => setShowModal(true)}
        className="empty-state-btn"
      >
        <FaPlus className="me-2" />
        Rekodi Mchango wa Kwanza
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="michango-loading">
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3">Inapakia michango...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="michango-container">
      <div className="container-fluid mt-4">
        <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>
          Usimamizi wa Michango
        </h1>

        {/* Action Buttons */}
        <div className="michango-actions">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowModal(true)}
            className="action-btn"
          >
            <FaPlus className="me-2" />
            Ongeza Mchango
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleExportToExcel}
            className="action-btn-outline"
          >
            <FaDownload className="me-2" />
            Ripoti Ya Excel
          </Button>
        </div>

        {/* Search Box - Only show if there are contributions */}
        {contributions.length > 0 && (
          <div className="michango-search">
            <Form.Group controlId="searchInput">
              <Form.Label className="fw-semibold">Tafuta Mchango</Form.Label>
              <div className="search-input-wrapper">
                <Form.Control
                  type="text"
                  placeholder="Tafuta kwa jina la mchangiaji..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="search-input"
                />
              </div>
            </Form.Group>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="mt-3" onClose={() => setError(null)} dismissible>
            <Alert.Heading>Hitilafu!</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" size="sm" onClick={fetchContributions}>
              Jaribu Tena
            </Button>
          </Alert>
        )}

        {/* Data Table */}
        <div className="michango-table-wrapper">
          {contributions.length > 0 ? (
            <StyledDataTable
              columns={columns}
              data={filteredData}
              paginationRowsPerPageOptions={[50, 100, 200, 500]}
              paginationPerPage={perPage}
              onChangeRowsPerPage={setPerPage}
              emptyMessage="Hakuna matokeo yanayolingana na utafutaji wako."
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>

      {/* Add Contribution Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title>
            <FaMoneyBillWave className="me-2" style={{ color: '#4169e1' }} />
            Rekodi Mchango Mpya
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <FaUser className="me-2" />
                Mchangiaji <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={newContribution.pledge_id}
                onChange={(e) => setNewContribution({ ...newContribution, pledge_id: e.target.value })}
                required
              >
                <option value="">Chagua Mchangiaji</option>
                {pledges.map(pledge => (
                  <option key={pledge.id} value={pledge.id}>
                    {pledge.full_name} - Ahadi: TZS {(parseFloat(pledge.amount) || 0).toLocaleString()}
                    (Imelipwa: TZS {(parseFloat(pledge.amount_collected) || 0).toLocaleString()})
                  </option>
                ))}
              </Form.Select>
              {pledges.length === 0 && (
                <Form.Text className="text-warning">
                  Hakuna waliotoa ahadi. Tafadhali ongeza ahadi kwanza.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <FaMoneyBillWave className="me-2" />
                Kiasi (TZS) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="Weka kiasi cha mchango"
                value={newContribution.amount}
                onChange={(e) => setNewContribution({ ...newContribution, amount: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Maelezo (Si Lazima)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Maelezo ya ziada kuhusu mchango"
                value={newContribution.description}
                onChange={(e) => setNewContribution({ ...newContribution, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <FaUserCheck className="me-2" />
                Aliyepokea
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Jina la aliyepokea mchango"
                value={newContribution.received_by}
                onChange={(e) => setNewContribution({ ...newContribution, received_by: e.target.value })}
              />
              <Form.Text className="text-muted">
                Acha tupu ili kutumia jina lako la mtumiaji
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Ghairi
          </Button>
          <Button
            variant="primary"
            onClick={handleAddContribution}
            disabled={submitting || pledges.length === 0}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : 'Rekodi Mchango'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Contribution Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title>
            <FaEdit className="me-2" style={{ color: '#4169e1' }} />
            Hariri Taarifa za Mchango
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedContribution && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Mchangiaji</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedContribution.full_name}
                  disabled
                  className="bg-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Kiasi (TZS)</Form.Label>
                <Form.Control
                  type="number"
                  value={selectedContribution.amount_raw}
                  onChange={(e) => setSelectedContribution({
                    ...selectedContribution,
                    amount_raw: parseFloat(e.target.value),
                    amount: `TZS ${parseFloat(e.target.value).toLocaleString()}`
                  })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Maelezo</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={selectedContribution.description !== '-' ? selectedContribution.description : ''}
                  onChange={(e) => setSelectedContribution({ ...selectedContribution, description: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Aliyepokea</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedContribution.received_by}
                  onChange={(e) => setSelectedContribution({ ...selectedContribution, received_by: e.target.value })}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Ghairi
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateContribution}
            disabled={submitting}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : 'Sasisha Mchango'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Contribution Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" />
            Futa Mchango
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedContribution && (
            <div>
              <p>Je, una uhakika unataka kufuta mchango huu?</p>
              <div className="contribution-details-card p-3 rounded">
                <div className="d-flex align-items-center mb-2">
                  <strong className="w-25">Mchangiaji:</strong>
                  <span>{selectedContribution.full_name}</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <strong className="w-25">Kiasi:</strong>
                  <span>{selectedContribution.amount}</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <strong className="w-25">Tarehe:</strong>
                  <span>{selectedContribution.date}</span>
                </div>
              </div>
              <p className="text-danger mt-3 mb-0">
                <strong>Kumbuka:</strong> Kitendo hiki hakiwezi kutenguliwa.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Ghairi
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteContribution}
            disabled={submitting}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : 'Futa Mchango'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Michango;